/**
 * Vue chat d'une conversation — messages + composer.
 * Polling 3s pour les messages (WhatsApp-perçu).
 */
import { useState, useEffect, useRef, useMemo } from 'react';
import {
  Send, Paperclip, Image as ImageIcon, X, Reply, Trash2, Pencil,
  Pin, MoreVertical, Smile, Clock, BellOff, Bell, Mic, Forward,
  ArrowLeft, Users as UsersIcon, Star,
} from 'lucide-react';
import ChatCheckMarks from '../../components/Messagerie/ChatCheckMarks';
import ChatEmojiPicker from '../../components/Messagerie/ChatEmojiPicker';
import ChatVoiceRecorder from '../../components/Messagerie/ChatVoiceRecorder';
import {
  useListerMessagesQuery, useEnvoyerMessageMutation,
  useMarquerLusMutation, useDetailConversationQuery,
  useToggleReactionMutation, useSupprimerMessageMutation,
  useEditerMessageMutation, useEpinglerMessageMutation,
  usePatcherConversationMutation, useCreerRappelMutation,
  useSignalerTypingMutation, useTypingStatusQuery,
  useTransfererMessageMutation, useListerConversationsQuery,
  type MessageItem,
} from '../../services/messagerieChat';

const EMOJIS_RAPIDES = ['👍', '❤️', '😂', '❓', '🙏', '✓'];

export function ChatView({ convId, onClose, onConvChange }: {
  convId: number;
  onClose: () => void;
  onConvChange?: () => void;
}) {
  const { data: conv, refetch: refetchConv } = useDetailConversationQuery(convId, {
    pollingInterval: 15000,
  });
  const { data: msgsData, refetch: refetchMsgs } = useListerMessagesQuery(
    { conv_id: convId, limit: 40 },
    { pollingInterval: 3000 }
  );
  const messages = msgsData?.results || [];

  const [envoyer, { isLoading: sending }] = useEnvoyerMessageMutation();
  const [marquerLus] = useMarquerLusMutation();
  const [toggleReaction] = useToggleReactionMutation();
  const [supprimer] = useSupprimerMessageMutation();
  const [editer] = useEditerMessageMutation();
  const [epingler] = useEpinglerMessageMutation();
  const [patchConv] = usePatcherConversationMutation();
  const [signalerTyping] = useSignalerTypingMutation();
  const [transferer] = useTransfererMessageMutation();
  const { data: typingData } = useTypingStatusQuery(convId, { pollingInterval: 4000 });
  const { data: mesConvs } = useListerConversationsQuery();
  const [voiceMode, setVoiceMode] = useState(false);
  const [transferSrc, setTransferSrc] = useState<MessageItem | null>(null);
  const [transferTargets, setTransferTargets] = useState<Set<number>>(new Set());
  const [transferring, setTransferring] = useState(false);
  const lastTypingRef = useRef<number>(0);
  const emitTyping = () => {
    const now = Date.now();
    if (now - lastTypingRef.current < 2500) return;
    lastTypingRef.current = now;
    signalerTyping(convId).catch(() => {});
  };
  const sendVoice = async (blob: Blob, mime: string, dureeMs: number) => {
    setVoiceMode(false);
    const ext = mime.includes('mp4') ? 'm4a' : mime.includes('ogg') ? 'ogg' : 'webm';
    const file = new File([blob], `vocal-${Date.now()}.${ext}`, { type: mime });
    try {
      await envoyer({
        conv_id: convId,
        contenu: `🎤 Message vocal · ${Math.round(dureeMs / 1000)}s`,
        fichier: file,
      } as any).unwrap();
    } catch (e: any) { alert(e?.data?.detail || 'Erreur envoi vocal'); }
  };
  const doTransfert = async () => {
    if (!transferSrc || transferTargets.size === 0) return;
    setTransferring(true);
    try {
      await transferer({ msg_id: transferSrc.id, conv_ids: Array.from(transferTargets) }).unwrap();
      setTransferSrc(null); setTransferTargets(new Set());
    } catch (e: any) { alert(e?.data?.detail || 'Erreur transfert'); }
    finally { setTransferring(false); }
  };

  const [texte, setTexte] = useState('');
  const [replyTo, setReplyTo] = useState<MessageItem | null>(null);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [showMenu, setShowMenu] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);
  const imgRef = useRef<HTMLInputElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);

  // Marquer lus à l'ouverture
  useEffect(() => {
    marquerLus(convId).catch(() => {});
  }, [convId, marquerLus]);

  // Scroll auto en bas quand nouveau message
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages.length]);

  const handleSend = async () => {
    if (!texte.trim()) return;
    try {
      if (editingId) {
        await editer({ msg_id: editingId, contenu: texte }).unwrap();
        setEditingId(null);
      } else {
        await envoyer({
          conv_id: convId,
          contenu: texte,
          reply_to_id: replyTo?.id,
        }).unwrap();
        setReplyTo(null);
      }
      setTexte('');
      onConvChange?.();
    } catch (e: any) {
      alert(e?.data?.detail || 'Erreur envoi');
    }
  };

  const handleFile = async (e: React.ChangeEvent<HTMLInputElement>, isImage: boolean) => {
    const f = e.target.files?.[0];
    if (!f) return;
    try {
      await envoyer({
        conv_id: convId,
        fichier: f,
        contenu: texte || undefined,
        reply_to_id: replyTo?.id,
      }).unwrap();
      setTexte('');
      setReplyTo(null);
      onConvChange?.();
    } catch (e: any) {
      alert(e?.data?.detail || 'Erreur envoi fichier');
    } finally {
      if (fileRef.current) fileRef.current.value = '';
      if (imgRef.current) imgRef.current.value = '';
    }
  };

  const messagesEpingles = useMemo(() => messages.filter(m => m.epingle), [messages]);

  return (
    <>
      {/* Header */}
      <div className="px-4 py-3 border-b border-slate-200 dark:border-gray-800 bg-white dark:bg-gray-950 flex items-center gap-3">
        <button onClick={onClose} className="p-1.5 hover:bg-slate-100 dark:hover:bg-gray-800 rounded lg:hidden">
          <ArrowLeft size={16} />
        </button>
        <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-bold ${
          conv?.type_conv === 'service' ? 'bg-blue-500' :
          conv?.type_conv === 'broadcast' ? 'bg-red-500' :
          conv?.type_conv === 'direct' ? 'bg-slate-400' : 'bg-emerald-500'
        }`}>
          {(conv?.titre_affichage || '?').charAt(0).toUpperCase()}
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-bold text-slate-900 dark:text-white truncate">
            {conv?.titre_affichage || '...'}
          </div>
          <div className="text-[11px] text-slate-500 truncate">
            {conv?.type_conv === 'direct'
              ? 'Conversation privée'
              : `${conv?.nb_participants || 0} participants`}
          </div>
        </div>
        <div className="relative">
          <button
            onClick={() => setShowMenu(!showMenu)}
            className="p-1.5 hover:bg-slate-100 dark:hover:bg-gray-800 rounded"
          >
            <MoreVertical size={16} />
          </button>
          {showMenu && (
            <div className="absolute right-0 top-full mt-1 bg-white dark:bg-gray-900 border border-slate-200 dark:border-gray-800 rounded shadow-lg py-1 text-xs z-20 min-w-[200px]">
              <button
                onClick={() => { patchConv({ id: convId, body: { epingle: !conv?.epingle } }); setShowMenu(false); }}
                className="w-full text-left px-3 py-1.5 hover:bg-slate-100 dark:hover:bg-gray-800 flex items-center gap-2"
              >
                <Pin size={12} /> {conv?.epingle ? 'Désépingler' : 'Épingler'}
              </button>
              <button
                onClick={() => { patchConv({ id: convId, body: { muted_jusqu_a: conv?.muted ? 'off' : '24h' } }); setShowMenu(false); }}
                className="w-full text-left px-3 py-1.5 hover:bg-slate-100 dark:hover:bg-gray-800 flex items-center gap-2"
              >
                {conv?.muted ? <><Bell size={12}/> Réactiver notifs</> : <><BellOff size={12}/> Muter 24h</>}
              </button>
              <button
                onClick={() => { patchConv({ id: convId, body: { archive: !conv?.archive } }); setShowMenu(false); onClose(); }}
                className="w-full text-left px-3 py-1.5 hover:bg-slate-100 dark:hover:bg-gray-800"
              >
                {conv?.archive ? 'Désarchiver' : 'Archiver'}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Messages épinglés */}
      {messagesEpingles.length > 0 && (
        <div className="px-4 py-1.5 bg-amber-50 dark:bg-amber-900/20 border-b border-amber-200 dark:border-amber-800/60 text-[11px] text-amber-800 dark:text-amber-200 flex items-center gap-2">
          <Pin size={11} />
          <span className="font-semibold">Épinglé :</span>
          <span className="truncate flex-1">{messagesEpingles[0].contenu || messagesEpingles[0].fichier_nom}</span>
        </div>
      )}

      {/* Messages */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-3 space-y-2 bg-slate-50 dark:bg-gray-900">
        {messages.map((m, idx) => {
          const precedent = idx > 0 ? messages[idx - 1] : null;
          const showAvatar = !precedent || precedent.expediteur_user_id !== m.expediteur_user_id;
          const showDate = !precedent ||
            new Date(precedent.date_envoi).toDateString() !== new Date(m.date_envoi).toDateString();
          return (
            <div key={m.id}>
              {showDate && <SeparateurDate date={m.date_envoi} />}
              <MessageBubble
                m={m}
                showAvatar={showAvatar}
                onReply={() => setReplyTo(m)}
                onReact={(emoji) => toggleReaction({ msg_id: m.id, emoji })}
                onDelete={() => window.confirm('Supprimer ce message ?') && supprimer(m.id)}
                onEdit={() => { setEditingId(m.id); setTexte(m.contenu); }}
                onEpingle={() => epingler(m.id)}
                onTransfer={() => { setTransferSrc(m); setTransferTargets(new Set()); }}
              />
            </div>
          );
        })}
        {messages.length === 0 && (
          <div className="text-center text-xs text-slate-500 py-10">
            Aucun message. Envoyez le premier !
          </div>
        )}
      </div>

      {/* Composer */}
      <div className="border-t border-slate-200 dark:border-gray-800 bg-white dark:bg-gray-950 p-3">
        {(replyTo || editingId) && (
          <div className="mb-2 p-2 bg-slate-100 dark:bg-gray-900 rounded flex items-start gap-2 text-xs">
            <Reply size={12} className="text-amber-600 mt-0.5" />
            <div className="flex-1 min-w-0">
              <div className="font-semibold text-slate-700 dark:text-gray-300">
                {editingId ? 'Édition du message' : `Réponse à ${replyTo?.expediteur_nom}`}
              </div>
              <div className="text-slate-500 truncate">{replyTo?.contenu || '...'}</div>
            </div>
            <button onClick={() => { setReplyTo(null); setEditingId(null); setTexte(''); }}
              className="text-slate-400 hover:text-red-500">
              <X size={13} />
            </button>
          </div>
        )}
        {typingData?.typing && typingData.typing.length > 0 && (
          <div className="px-1 py-0.5 text-[11px] italic text-slate-500 dark:text-gray-400">
            {typingData.typing.length === 1
              ? `${typingData.typing[0].user_nom} écrit…`
              : `${typingData.typing.length} personnes écrivent…`}
          </div>
        )}
        {voiceMode ? (
          <ChatVoiceRecorder onCancel={() => setVoiceMode(false)} onSend={sendVoice} />
        ) : (
          <div className="flex items-end gap-2">
            <input ref={fileRef} type="file" hidden onChange={(e) => handleFile(e, false)}
              accept=".pdf,.doc,.docx,.xls,.xlsx,.txt,.csv" />
            <input ref={imgRef} type="file" hidden onChange={(e) => handleFile(e, true)}
              accept="image/*" />
            <button onClick={() => imgRef.current?.click()} disabled={sending}
              className="p-2 text-slate-500 hover:bg-slate-100 dark:hover:bg-gray-800 rounded flex-shrink-0" title="Envoyer image">
              <ImageIcon size={16} />
            </button>
            <button onClick={() => fileRef.current?.click()} disabled={sending}
              className="p-2 text-slate-500 hover:bg-slate-100 dark:hover:bg-gray-800 rounded flex-shrink-0" title="Envoyer fichier">
              <Paperclip size={16} />
            </button>
            <ChatEmojiPicker onSelect={(e) => setTexte(t => t + e)} />
            <textarea
              value={texte}
              onChange={(e) => { setTexte(e.target.value); emitTyping(); }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); }
              }}
              placeholder="Écrivez un message..."
              rows={1}
              className="flex-1 bg-slate-100 dark:bg-gray-900 border-0 rounded-lg px-3 py-2 text-sm text-slate-900 dark:text-white focus:ring-2 focus:ring-amber-500 resize-none overflow-y-auto"
              style={{ minHeight: '38px', maxHeight: '120px' }}
            />
            {texte.trim() ? (
              <button onClick={handleSend} disabled={sending}
                className="p-2 bg-amber-600 hover:bg-amber-700 disabled:bg-slate-300 disabled:cursor-not-allowed text-white rounded-lg flex-shrink-0">
                <Send size={16} />
              </button>
            ) : (
              <button onClick={() => setVoiceMode(true)}
                className="p-2 text-slate-500 hover:bg-slate-100 dark:hover:bg-gray-800 rounded flex-shrink-0" title="Message vocal">
                <Mic size={16} />
              </button>
            )}
          </div>
        )}
      </div>
      {transferSrc && (
        <div className="fixed inset-0 z-[75] bg-black/70 flex items-end sm:items-center justify-center p-3"
          onClick={() => !transferring && setTransferSrc(null)}>
          <div onClick={(e) => e.stopPropagation()}
            className="w-full max-w-md rounded-xl shadow-2xl max-h-[85vh] flex flex-col bg-white dark:bg-gray-900 text-slate-900 dark:text-gray-100">
            <div className="px-4 py-3 border-b border-slate-200 dark:border-gray-800 flex items-center justify-between">
              <h3 className="font-semibold text-sm">Transférer</h3>
              <button onClick={() => setTransferSrc(null)} className="text-slate-400"><X size={18} /></button>
            </div>
            <div className="flex-1 overflow-y-auto p-2 divide-y divide-slate-100 dark:divide-gray-800">
              {(mesConvs?.results || []).filter(c => c.id !== transferSrc.conversation_id).map(c => {
                const sel = transferTargets.has(c.id);
                return (
                  <button key={c.id}
                    onClick={() => setTransferTargets(prev => {
                      const n = new Set(prev);
                      if (n.has(c.id)) n.delete(c.id); else n.add(c.id);
                      return n;
                    })}
                    className={`w-full p-2.5 flex items-center gap-3 text-left hover:bg-slate-50 dark:hover:bg-gray-800 ${sel ? 'bg-blue-50 dark:bg-blue-900/40' : ''}`}>
                    <div className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 ${sel ? 'border-blue-500 bg-blue-500' : 'border-slate-300 dark:border-gray-600'}`}>
                      {sel && <svg width="12" height="12" viewBox="0 0 12 12"><path d="M2 6l3 3 5-6" stroke="white" strokeWidth="2" fill="none" strokeLinecap="round" /></svg>}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium truncate">{c.titre_affichage}</div>
                      <div className="text-[10px] text-slate-500">{c.type_conv}</div>
                    </div>
                  </button>
                );
              })}
            </div>
            <div className="px-4 py-3 border-t border-slate-200 dark:border-gray-800 flex justify-between items-center gap-2">
              <span className="text-[11px] text-slate-500">{transferTargets.size} destination(s)</span>
              <div className="flex gap-2">
                <button onClick={() => setTransferSrc(null)} disabled={transferring}
                  className="px-3 py-1.5 text-sm bg-slate-100 dark:bg-gray-800 text-slate-700 dark:text-gray-200 hover:bg-slate-200 rounded">
                  Annuler
                </button>
                <button onClick={doTransfert} disabled={transferring || transferTargets.size === 0}
                  className="inline-flex items-center gap-1 px-4 py-1.5 text-sm bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white rounded font-semibold">
                  <Forward size={13} />
                  {transferring ? 'Envoi…' : 'Transférer'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function MessageBubble({ m, showAvatar, onReply, onReact, onDelete, onEdit, onEpingle, onTransfer }: {
  m: MessageItem;
  showAvatar: boolean;
  onReply: () => void;
  onReact: (emoji: string) => void;
  onDelete: () => void;
  onEdit: () => void;
  onEpingle: () => void;
  onTransfer: () => void;
}) {
  const [hover, setHover] = useState(false);
  const [showReactions, setShowReactions] = useState(false);
  const [showMenu, setShowMenu] = useState(false);

  if (m.type_msg === 'systeme') {
    return (
      <div className="text-center text-[11px] text-slate-500 italic py-1">
        {m.contenu}
      </div>
    );
  }

  const mine = m.a_moi;

  return (
    <div className={`flex ${mine ? 'justify-end' : 'justify-start'} group`}
      onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)}>
      {!mine && showAvatar && (
        <div className="w-7 h-7 rounded-full bg-slate-300 dark:bg-gray-700 flex-shrink-0 mr-2 flex items-center justify-center text-[10px] text-white font-bold">
          {m.expediteur_nom.charAt(0)}
        </div>
      )}
      {!mine && !showAvatar && <div className="w-7 mr-2" />}
      <div className={`max-w-[70%] ${mine ? 'items-end' : 'items-start'} flex flex-col relative`}>
        {!mine && showAvatar && (
          <div className="text-[10px] text-slate-500 font-medium mb-0.5 ml-1">
            {m.expediteur_nom}
          </div>
        )}

        {m.reply_to && (
          <div className={`mb-0.5 px-2 py-1 text-[11px] rounded border-l-2 max-w-full ${
            mine ? 'bg-amber-50 border-amber-500 text-amber-800' : 'bg-slate-100 border-slate-400 text-slate-700 dark:bg-gray-800 dark:text-gray-300'
          }`}>
            <div className="font-semibold">{m.reply_to.expediteur_nom}</div>
            <div className="truncate">{m.reply_to.contenu}</div>
          </div>
        )}

        <div className={`px-3 py-1.5 rounded-lg text-sm relative ${
          m.important ? 'ring-2 ring-red-500' : ''
        } ${
          mine ? 'bg-amber-600 text-white' : 'bg-white dark:bg-gray-800 text-slate-900 dark:text-white shadow-sm'
        }`}>
          {m.supprime ? (
            <span className="italic opacity-70">Message supprimé</span>
          ) : m.type_msg === 'image' && m.fichier_url ? (
            <div>
              <img src={m.fichier_url} alt={m.fichier_nom} className="max-w-full max-h-64 rounded" />
              {m.contenu && <div className="mt-1">{m.contenu}</div>}
            </div>
          ) : m.type_msg === 'fichier' && m.fichier_url && m.fichier_mime?.startsWith('audio/') ? (
            <div className={`inline-flex items-center gap-1.5 ${mine ? 'text-white' : ''}`}>
              <Mic size={12} className="flex-shrink-0" />
              <audio src={m.fichier_url} controls preload="metadata" className="h-8 max-w-[220px]" />
            </div>
          ) : m.type_msg === 'fichier' && m.fichier_url ? (
            <a href={m.fichier_url} target="_blank" rel="noopener noreferrer"
              className={`flex items-center gap-2 ${mine ? 'text-white' : 'text-blue-600'}`}>
              <Paperclip size={14} />
              <div>
                <div className="font-medium">{m.fichier_nom}</div>
                <div className="text-[10px] opacity-80">
                  {m.fichier_taille ? `${Math.round(m.fichier_taille / 1024)} Ko` : ''}
                </div>
              </div>
            </a>
          ) : (
            <div className="whitespace-pre-wrap break-words">{m.contenu}</div>
          )}
          <div className={`text-[9px] mt-0.5 flex items-center gap-1 ${mine ? 'text-amber-100 justify-end' : 'text-slate-400'}`}>
            {m.modifie && <span>modifié</span>}
            {formatHeure(m.date_envoi)}
            <ChatCheckMarks
              mine={mine}
              myUserId={m.expediteur_user_id}
              livre_a={m.livre_a}
              lu_par={m.lu_par}
              isDark={false}
            />
          </div>
        </div>

        {/* Réactions affichées */}
        {(m.reactions?.length || 0) > 0 && (
          <div className="flex flex-wrap gap-1 mt-0.5">
            {m.reactions!.map(r => (
              <button key={r.emoji} onClick={() => onReact(r.emoji)}
                title={r.users.map(u => u.user_nom).join(', ')}
                className={`text-[11px] px-1.5 py-0.5 rounded-full border ${
                  r.moi
                    ? 'bg-amber-100 border-amber-500 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200'
                    : 'bg-white dark:bg-gray-800 border-slate-200 dark:border-gray-700'
                }`}>
                {r.emoji} <span className="text-[9px]">{r.users.length}</span>
              </button>
            ))}
          </div>
        )}

        {/* Menu contextuel au survol */}
        {hover && !m.supprime && (
          <div className={`absolute ${mine ? 'right-0' : 'left-0'} -top-8 bg-white dark:bg-gray-800 border border-slate-200 dark:border-gray-700 rounded shadow-lg flex text-xs`}>
            <button onMouseEnter={() => setShowReactions(true)}
              onMouseLeave={() => setTimeout(() => setShowReactions(false), 500)}
              className="p-1.5 hover:bg-slate-100 dark:hover:bg-gray-700" title="Réagir">
              <Smile size={12} />
            </button>
            <button onClick={onReply} className="p-1.5 hover:bg-slate-100 dark:hover:bg-gray-700" title="Répondre">
              <Reply size={12} />
            </button>
            <button onClick={onTransfer} className="p-1.5 hover:bg-slate-100 dark:hover:bg-gray-700" title="Transférer">
              <Forward size={12} />
            </button>
            <button onClick={onEpingle} className="p-1.5 hover:bg-slate-100 dark:hover:bg-gray-700" title="Épingler">
              <Pin size={12} />
            </button>
            {mine && m.type_msg === 'texte' && (
              <button onClick={onEdit} className="p-1.5 hover:bg-slate-100 dark:hover:bg-gray-700" title="Éditer">
                <Pencil size={12} />
              </button>
            )}
            {mine && (
              <button onClick={onDelete} className="p-1.5 hover:bg-slate-100 dark:hover:bg-gray-700 text-red-500" title="Supprimer">
                <Trash2 size={12} />
              </button>
            )}
          </div>
        )}
        {showReactions && (
          <div className={`absolute ${mine ? 'right-0' : 'left-0'} -top-16 bg-white dark:bg-gray-800 border border-slate-200 dark:border-gray-700 rounded-full shadow-lg flex p-1 gap-1 z-10`}
            onMouseEnter={() => setShowReactions(true)} onMouseLeave={() => setShowReactions(false)}>
            {EMOJIS_RAPIDES.map(e => (
              <button key={e} onClick={() => { onReact(e); setShowReactions(false); }}
                className="text-lg hover:scale-125 transition-transform">
                {e}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function SeparateurDate({ date }: { date: string }) {
  const d = new Date(date);
  const now = new Date();
  const sameDay = d.toDateString() === now.toDateString();
  const yesterday = new Date(now); yesterday.setDate(now.getDate() - 1);
  const isYesterday = d.toDateString() === yesterday.toDateString();
  const label = sameDay ? "Aujourd'hui" : isYesterday ? 'Hier' : d.toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' });
  return (
    <div className="flex justify-center my-2">
      <span className="text-[10px] uppercase tracking-wider bg-slate-200 dark:bg-gray-800 text-slate-600 dark:text-gray-400 px-2 py-0.5 rounded-full">
        {label}
      </span>
    </div>
  );
}

function formatHeure(iso: string): string {
  return new Date(iso).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
}
